import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp.tsx";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { OTPsSendResponse } from "@stytch/vanilla-js";
import { useStytch } from "@stytch/react";
import { getStytchErrorMessage } from "@/lib/utils/stytch-errors";

interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  otpResponse: OTPsSendResponse | null;
  setOtpResponse: (argo0: OTPsSendResponse) => void;
  phoneNumber: string;
  handleSuccess: (otpResponse: OTPsSendResponse) => void;
}

export function VerificationModal({
  open,
  onClose,
  otpResponse,
  setOtpResponse,
  phoneNumber,
  handleSuccess,
}: VerificationModalProps) {
  const stytchClient = useStytch();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");

  const resendSms = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await stytchClient.otps.sms.send(phoneNumber, {
        expiration_minutes: 5,
      });
      setOtpResponse(response);
    } catch (e) {
      console.error(e);
      setError(getStytchErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [stytchClient, phoneNumber, setOtpResponse]);

  const verifyOtp = useCallback(async () => {
    if (!otpResponse || !otpResponse.method_id) {
      return;
    }

    if (!otp) {
      return;
    }

    setError(null);
    try {
      setLoading(true);
      const response = await stytchClient.otps.authenticate(
        otp,
        otpResponse.method_id,
        {
          session_duration_minutes: 60 * 24, // 1 day
        },
      );

      if (response.session?.session_id) {
        handleSuccess(response);
      }
    } catch (e) {
      console.error(e);
      setError(getStytchErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [otp, otpResponse, stytchClient, handleSuccess]);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
          setOtp("");
          setError(null);
        }
      }}
    >
      <DialogContent
        className="w-full max-w-[450px] p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b-[1px] border-[#E5E7EB] p-4">
          <DialogTitle className="text-2xl">Verification</DialogTitle>
          <DialogDescription className="text-sm">
            Enter the code sent to your mobile phone
          </DialogDescription>
        </DialogHeader>
        <div className="mx-auto flex w-full max-w-[410px] flex-col items-center gap-4 p-4">
          <img src="assets/verification-icon.png" alt="Icon" className="mb-6" />

          {error && (
            <div className="rounded-md bg-red-200 p-4 text-sm font-medium text-red-500">
              It looks like that code didn&apos;t work.{" "}
              <span className="cursor-pointer underline" onClick={resendSms}>
                Click here to try again
              </span>
            </div>
          )}

          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(value) => setOtp(value)}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} className="h-14 w-14 text-lg" />
            </InputOTPGroup>
          </InputOTP>

          <Button
            className="h-12 w-full rounded-lg text-lg font-bold"
            onClick={verifyOtp}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>

          <p
            className="text-md mt-2 cursor-pointer font-semibold text-gray-400"
            onClick={resendSms}
          >
            Didn&apos;t receive a code? Click here to try again
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
